// Native angular modules
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, Injector, ErrorHandler } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';

// Custom module
import { AppRoutingModule } from './common/modules/app-routing.module';

// Services
import { HttpRequestService } from './common/services/http-request.service';
import { AppConfigService } from './common/services/app-config.service';
import { AuthInterceptor } from './common/services/auth-interceptor';
import { MessageService, ConfirmationService } from 'primeng/api';

import { Utils } from './common/services/utils.service';
import { LogService } from './common/services/log.service';

// Components
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { DashboardComponent } from './dashboard/dashboard.component';

// 3rd party
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { OverlayModule } from '@angular/cdk/overlay';
import { PrimengModule } from './common/modules/primeng/primeng.module';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { RxMethods } from './common/services/rxjs.service';
import { LOCATION_INITIALIZED } from '@angular/common';
import { AuthModule } from './auth/auth.module';
import { ResolverService } from './common/services/resolver.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ChangePasswordComponent } from './home/change-password/change-password.component';
import { AppSharedModule } from './common/modules/shared.module';
import { LoginComponent } from './login/login.component';
import { GlobalErrorHandler } from './common/services/error-handler';
import { FenceModalComponent } from './masters/location/fence-modal/fence-modal.component';
import { LocationCreateService } from './masters/location/location-create/location-create.service';
import { ShuttleModalComponent } from './masters/shuttle-route/shuttle-modal/shuttle-modal.component';
import { TripCancelComponent } from './masters/shuttle-route/trip-cancel/trip-cancel.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { CheckForUpdateService } from './common/services/check-for-update.service';
import { RouteCreateService } from './masters/route/route-create/route-create.service';
import { RouteListService } from './masters/route/route-list/route-list.service';
import { PmsIntegratorModule } from 'pms-integrator';
import { PmsComponent } from './home/pms/pms.component';
import { DriverSummaryService } from './masters/driver/driver-summary/driver-summary.service';
import { WeatherComponent } from './home/weather/weather.component';
import { DataprivacyComponent } from './home/dataprivacy/dataprivacy.component';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/lang/', '-lang.json');
}

@NgModule({
  entryComponents: [
    ChangePasswordComponent,
    FenceModalComponent,
    ShuttleModalComponent,
    TripCancelComponent,
    WeatherComponent,
    DataprivacyComponent
  ],
  declarations: [
    AppComponent,
    DashboardComponent,
    HomeComponent,
    ChangePasswordComponent,
    LoginComponent,
    FenceModalComponent,
    ShuttleModalComponent,
    TripCancelComponent,
    PmsComponent,
    WeatherComponent,
    DataprivacyComponent
    ],
  imports: [
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    ReactiveFormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    OverlayModule,
    NgxUiLoaderModule,
    AuthModule,
    ScrollingModule,
    AppSharedModule,
    PrimengModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    PmsIntegratorModule
  ],
  exports: [
  ],
  providers: [
    Utils,
    {
      provide: APP_INITIALIZER,
      useFactory: AppLoader,
      deps: [AppConfigService],
      multi: true
    }, {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true
    },
    HttpRequestService,
    AppConfigService,
    MessageService,
    RxMethods,
    ResolverService,
    ConfirmationService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    LocationCreateService,
    CheckForUpdateService,
    RouteCreateService,
    RouteListService,
    LogService,
    DriverSummaryService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

export function AppLoader(_appConfig: AppConfigService) {
  return () => _appConfig.getAppConfigs();
}

export function appInitializerFactory(translate: TranslateService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      const langToSet = 'en-GB'
      translate.setDefaultLang('en-US');
      translate.use(langToSet).subscribe(() => {
        console.info(`Successfully initialized '${langToSet}' language.'`);
      }, err => {
        console.error(`Problem with '${langToSet}' language initialization.'`);
      }, () => {
        resolve(null);
      });
    });
  });
}
